import type { MouseEvent, ReactNode } from 'react';
import { useGT } from 'gt-react';
import {
  CheckIcon,
  MinusIcon,
  StackIcon,
  StackMinusIcon,
  WarningIcon,
  XIcon,
} from '@phosphor-icons/react';
import { formatCurrency } from '@erp/shared-utils';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type {
  OrderReadiness,
  ReadinessState,
  ThresholdCheck,
} from '@erp/shared-utils';
import {
  incrementCheckRowValue,
  moqCheckRowValue,
  readinessHoverSummary,
  readinessStateLabel,
} from '../utils/orderReadinessCopy';

const STATE_STYLE: Record<
  ReadinessState,
  { icon: typeof StackIcon; tone: string }
> = {
  ready: { icon: StackIcon, tone: 'text-green-700 hover:text-green-700' },
  warning: {
    icon: StackMinusIcon,
    tone: 'text-amber-700 hover:text-amber-700',
  },
  not_ready: {
    icon: StackMinusIcon,
    tone: 'text-red-600 hover:text-red-600',
  },
};

/** Ready, but met within the buffer: still green, the minus-stack variant. */
const WITHIN_BUFFER_STYLE = {
  icon: StackMinusIcon,
  tone: 'text-green-700 hover:text-green-700',
};

function stateStyle(readiness: OrderReadiness) {
  return readiness.withinBuffer
    ? WITHIN_BUFFER_STYLE
    : STATE_STYLE[readiness.state];
}

/**
 * Rows select on click; the icon and its popover (portalled, but still in the
 * React tree) must not.
 */
function stopRowSelect(e: MouseEvent): void {
  e.stopPropagation();
}

/**
 * The order's readiness as one Stack icon (green ready, amber warning,
 * red not ready), with a one-line reason on hover and the checks in a popover.
 * `showLabel` adds the state word next to the icon, for the detail bar.
 */
export function OrderReadinessButton({
  readiness,
  showLabel = false,
}: {
  readiness: OrderReadiness;
  showLabel?: boolean;
}) {
  const gt = useGT();
  const { icon: Icon, tone } = stateStyle(readiness);
  const title = readinessHoverSummary(gt, readiness);
  const label = readinessStateLabel(gt, readiness);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size={showLabel ? 'sm' : 'icon'}
          className={cn(
            'shrink-0 text-xs',
            showLabel ? 'h-6 gap-1 px-1.5' : 'h-6 w-6',
            tone,
          )}
          title={title}
          aria-label={title}
          onClick={stopRowSelect}
        >
          <Icon weight="bold" className="h-[18px] w-[18px]" />
          {showLabel && label}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 p-0"
        onClick={stopRowSelect}
      >
        <ReadinessDetail readiness={readiness} />
      </PopoverContent>
    </Popover>
  );
}

function ReadinessDetail({ readiness }: { readiness: OrderReadiness }) {
  const gt = useGT();
  const { icon: Icon, tone } = stateStyle(readiness);
  const { mov, moq, freight, increment } = readiness;
  const anyDefault = mov.source === 'default' || freight.source === 'default';
  const label = readinessStateLabel(gt, readiness);
  return (
    <div className="text-xs">
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <Icon weight="bold" className={cn('h-4 w-4', tone)} />
        <span className={cn('font-medium', tone)}>{label}</span>
        <span className="ml-auto tabular-nums text-muted-foreground">
          {formatCurrency(readiness.total)}
        </span>
      </div>
      <Section
        title={gt('Can order', {
          context: 'readiness popover section: firm vendor requirements',
        })}
      >
        <ThresholdRow
          label={gt('Min order value', {
            context: 'readiness popover row: vendor minimum order value (MOV)',
          })}
          check={mov}
          firm={mov.source === 'vendor'}
          bufferPct={readiness.bufferPct}
        />
      </Section>
      <Section
        title={gt('Should meet', {
          context:
            'readiness popover section: soft targets; missing one costs extra but the order still goes',
        })}
      >
        <CheckRow
          status={moq.met ? 'met' : 'soft'}
          label={gt('Min order qty', {
            context: 'readiness popover row: vendor minimum order quantity',
          })}
          value={moqCheckRowValue(gt, moq)}
        />
        <CheckRow
          status={increment.met ? 'met' : 'soft'}
          label={gt('Pack multiple', {
            context: 'readiness popover row: vendor order increment',
          })}
          value={incrementCheckRowValue(gt, increment)}
        />
        <ThresholdRow
          label={gt('Prepaid freight', {
            context: 'readiness popover row: vendor prepaid-freight threshold',
          })}
          check={freight}
          firm={false}
          bufferPct={readiness.bufferPct}
        />
      </Section>
      {anyDefault && (
        <p className="border-t px-3 py-2 text-muted-foreground">
          {gt('* Default amount: the vendor has none on file.', {
            context: 'readiness popover footnote for default thresholds',
          })}
        </p>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="px-3 py-2">
      <div className="mb-1 font-medium text-muted-foreground">{title}</div>
      <div className="grid grid-cols-[1rem_1fr_auto] items-center gap-x-2 gap-y-1">
        {children}
      </div>
    </div>
  );
}

function ThresholdRow({
  label,
  check,
  firm,
  bufferPct,
}: {
  label: string;
  check: ThresholdCheck;
  firm: boolean;
  bufferPct: number;
}) {
  const gt = useGT();
  const amount = `${formatCurrency(check.amount)}${check.source === 'default' ? '*' : ''}`;
  return (
    <CheckRow
      status={
        check.inBuffer ? 'buffer' : check.met ? 'met' : firm ? 'firm' : 'soft'
      }
      label={`${label} ${amount}`}
      value={thresholdRowValue(gt, check, bufferPct)}
    />
  );
}

function thresholdRowValue(
  gt: ReturnType<typeof useGT>,
  check: ThresholdCheck,
  bufferPct: number,
): string {
  if (check.inBuffer) {
    return gt('{cushion} over, within {pct}% buffer', {
      context:
        'readiness popover: threshold met, but by less than the org buffer; a cancelled line could drop it under',
      cushion: formatCurrency(check.cushion),
      pct: String(bufferPct),
    });
  }
  return check.met
    ? gt('met', { context: 'readiness popover: threshold reached' })
    : gt('{gap} short', {
        context: 'readiness popover: dollars still missing',
        gap: formatCurrency(check.gap),
      });
}

const ROW_STATUS = {
  met: { icon: CheckIcon, tone: 'text-green-700' },
  buffer: { icon: MinusIcon, tone: 'text-green-700' },
  firm: { icon: XIcon, tone: 'text-red-600' },
  soft: { icon: WarningIcon, tone: 'text-amber-700' },
} as const;

function CheckRow({
  status,
  label,
  value,
}: {
  status: keyof typeof ROW_STATUS;
  label: string;
  value: string;
}) {
  const { icon: Icon, tone } = ROW_STATUS[status];
  return (
    <>
      <Icon aria-hidden weight="bold" className={cn('h-3.5 w-3.5', tone)} />
      <span>{label}</span>
      <span
        className={cn(
          'text-right tabular-nums',
          status === 'met' || status === 'buffer'
            ? 'text-muted-foreground'
            : tone,
        )}
      >
        {value}
      </span>
    </>
  );
}
